import React, { useEffect, useRef } from 'react';
import { LocationStoreView } from '../../types/locations';
import { LocationStore } from '../../location-store';
import { LocationSelectFormattedLocationDisplay } from '../common/location-select-formatted-location-display';
import { Box } from '@cad-arc';
import { observer } from 'mobx-react';
import { CommonSize } from '../../../common/helpers/utils';
import { getLocationOptionAriaLabel } from '../../helpers/location-display';

interface AddressRowProps {
    row: LocationStoreView;
    query: string;
    onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    index: number;
    store: LocationStore;
    mode?: 'LIST' | 'MAP';
    size?: CommonSize;
    optionIdPrefix: string;
}

export const LocationListRow = observer(
    ({ row, onClick, query, store, index, mode, size, optionIdPrefix }: AddressRowProps) => {
        const ref = useRef<HTMLDivElement | null>(null);
        const isSelected = index === store.highlightedLocationIndex;
        const optionLabelId = `${optionIdPrefix}-${row.id}-label`;

        useEffect(() => {
            if (
                ref.current &&
                store.highlightedLocationIndex === index &&
                store.shouldScrollLocationList
            ) {
                ref.current.scrollIntoView(false);
            }
        }, [store.highlightedLocationIndex, index, store.shouldScrollLocationList]);

        return (
            <Box
                id={`${optionIdPrefix}-${row.id}`}
                role="option"
                aria-selected={isSelected}
                aria-labelledby={optionLabelId}
                onMouseEnter={() => {
                    store.setHighlightedLocationIndex(index, { source: 'mouse' });
                }}
                style={{
                    backgroundColor: isSelected ? 'var(--arc-colors-interactive-hover)' : undefined,
                }}
                data-testid={`address-row-${row.id}`}
                key={row.id}
                onMouseDown={(e) => onClick(e)}
                ref={ref}
                p={3}
            >
                <Box
                    id={optionLabelId}
                    style={{
                        position: 'absolute',
                        width: '1px',
                        height: '1px',
                        padding: 0,
                        margin: '-1px',
                        overflow: 'hidden',
                        clip: 'rect(0, 0, 0, 0)',
                        whiteSpace: 'nowrap',
                        border: 0,
                    }}
                >
                    {getLocationOptionAriaLabel(row)}
                </Box>
                <LocationSelectFormattedLocationDisplay
                    location={row}
                    query={query}
                    size={size}
                    showMapKeyMarker={mode === 'MAP'}
                    index={index}
                    isSelected={isSelected}
                />
            </Box>
        );
    }
);
